import { ToolIcon } from '@/components/ToolIcon';
import { formatBytes } from '@/lib/format';
import { say } from '@/lib/vocabulary';
import { toolLabel } from '@/lib/toolLabel';
import { cn } from '@/lib/cn';

/**
 * What is in this package, shown rather than listed.
 *
 * The discovery payload is the most important thing an operator reads and it
 * was rendered as a key/value dump - tool ids, byte counts, enum names. The
 * same facts, unchanged, now read as the thing they describe: one bar for the
 * whole bucket split into what was attributed and what was not, then each
 * tool with its own mark, its share of the package, and its size.
 *
 * Every number here is the API's own. Nothing is estimated, and the
 * unattributed remainder is drawn at the same weight as the tools because it
 * is usually the larger share and hiding it would flatter the result.
 */

interface Tool {
  toolId: string;
  label: string;
  iconSlug: string | null;
  domain: string | null;
  objects: number;
  bytes: number;
}

interface Stray {
  reason: string;
  objects: number;
  bytes: number;
}

export interface Contents {
  bucketName?: string;
  tools: Tool[];
  strays: Stray[];
  inventoryTotal: number;
  attributed: number;
  stray: number;
  /** Null when the payload carried no equation at all. */
  reconciled: boolean | null;
  byteTotal: number;
}

/** Reads the discovery payload without assuming more shape than it has. */
export function readContents(value: unknown): Contents | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const c = (v.conservation ?? {}) as Record<string, unknown>;
  if (!Array.isArray(v.tools)) return null;
  const num = (x: unknown) => (typeof x === 'number' ? x : 0);
  return {
    bucketName: typeof v.bucketName === 'string' ? v.bucketName : undefined,
    tools: (v.tools as Record<string, unknown>[]).map((t) => ({
      toolId: String(t.toolId ?? ''),
      // The discovery payload carries no display block, so falling back to
      // the raw id printed "microsoft_outlook" beside a walkthrough calling
      // the same tool "Microsoft Outlook", on a screen a buyer reads.
      label: t.label ? String(t.label) : t.toolId ? toolLabel(String(t.toolId)) : '',
      iconSlug: typeof t.iconSlug === 'string' ? t.iconSlug : null,
      domain: typeof t.domain === 'string' ? t.domain : null,
      objects: num(t.objects),
      bytes: num(t.bytes),
    })),
    strays: Array.isArray(v.strays)
      ? (v.strays as Record<string, unknown>[]).map((s) => ({
          reason: String(s.reason ?? ''),
          objects: num(s.objects),
          bytes: num(s.bytes),
        }))
      : [],
    inventoryTotal: num(c.inventoryTotal),
    attributed: num(c.attributed),
    stray: num(c.stray),
    reconciled: typeof c.reconciled === 'boolean' ? c.reconciled : null,
    byteTotal: num(v.byteTotal),
  };
}

function Bar({ share, tone }: { share: number; tone: 'tool' | 'stray' }) {
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
      <span
        className={cn('block h-full rounded-full', tone === 'tool' ? 'bg-acc' : 'bg-line-2')}
        style={{ width: `${Math.max(share * 100, share > 0 ? 1.5 : 0)}%` }}
      />
    </span>
  );
}

export function PackageContents({ contents }: { contents: Contents }) {
  const { tools, strays, inventoryTotal, attributed, stray, reconciled, byteTotal } = contents;
  // A discovery that has not run, or one that returned nothing, has no
  // population to take a percentage of. The fallback denominator of 1 turned
  // that into "0 files, 0.0% traced", which is a measurement of an empty
  // corpus rather than an admission that nothing has been counted.
  const counted = inventoryTotal || attributed + stray;
  const identified = counted > 0 ? (attributed / counted) * 100 : null;
  const largest = Math.max(...tools.map((t) => t.objects), ...strays.map((s) => s.objects), 1);

  return (
    <div className="px-4 py-3.5">
      {/* What you have, and how much of it we could identify. */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        {counted === 0 ? (
          <span className="text-small text-ink-2">
            Nothing has been counted for this run yet, so there is no population to describe.
          </span>
        ) : (
          <>
            <span className="flex items-baseline gap-2">
              <span className="mono text-stat text-ink">{inventoryTotal.toLocaleString()}</span>
              <span className="text-small text-ink-3">files</span>
            </span>
            <span className="mono text-row text-ink-2">{formatBytes(byteTotal)}</span>
            <span className="ml-auto text-small text-ink-2">
              <span className={cn('mono', (identified ?? 0) >= 50 ? 'text-ok' : 'text-warn')}>
                {identified!.toFixed(1)}%
              </span>{' '}
              traced to the software that made it
            </span>
          </>
        )}
      </div>

      {/* An absent equation and a failing one are opposite facts. Reading a
          missing field as a failure raised the alarm meant for a broken
          package on a payload that simply did not carry the check. */}
      <p className="mt-1 text-small text-ink-2">
        {reconciled === true
          ? `Everything ships: the ${stray.toLocaleString()} files we could not trace are delivered too, labelled with why. Every file is accounted for.`
          : reconciled === false
            ? 'The counts do not add up, which means this package cannot be trusted to be complete.'
            : 'This bucket reported no completeness check, so whether every file is accounted for is unknown here.'}
      </p>

      {counted > 0 && (
        <span className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-panel-2" aria-hidden="true">
          <span className="block h-full bg-acc" style={{ width: `${(attributed / counted) * 100}%` }} />
          <span className="block h-full bg-line-2" style={{ width: `${(stray / counted) * 100}%` }} />
        </span>
      )}

      {/* Each tool, with its own mark. */}
      <div className="mt-4 flex flex-col gap-2.5">
        {tools.map((t) => (
          <div key={t.toolId} className="flex items-center gap-3">
            <ToolIcon
              tool={t.toolId}
              display={{ label: t.label, iconSlug: t.iconSlug, domain: t.domain }}
              size={18}
            />
            <span className="w-44 flex-none truncate text-small text-ink">{t.label}</span>
            <span className="min-w-0 flex-1">
              <Bar share={t.objects / largest} tone="tool" />
            </span>
            <span className="mono w-24 flex-none text-right text-caption text-ink-2">
              {t.objects.toLocaleString()} files
            </span>
            <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(t.bytes)}</span>
          </div>
        ))}

        {strays.map((s) => (
          <div key={s.reason} className="flex items-center gap-3">
            <span
              className="grid h-[18px] w-[18px] flex-none place-items-center rounded-sm border border-line-2 text-caption text-ink-4"
              aria-hidden="true"
            >
              ?
            </span>
            <span className="w-44 flex-none truncate text-small text-ink-3" title={s.reason}>
              {say(s.reason)}
            </span>
            <span className="min-w-0 flex-1">
              <Bar share={s.objects / largest} tone="stray" />
            </span>
            <span className="mono w-24 flex-none text-right text-caption text-ink-2">
              {s.objects.toLocaleString()} files
            </span>
            <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(s.bytes)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
